import { JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { GapDocumentationItem, GapDocumentationType } from "./types";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionList,
  ActionListItem,
  BasicButton,
  Dialog,
  Link,
  Modal,
  ModalTrigger,
  RequirementsList,
  Stack,
  Text,
} from "@certn/cascadia";
import isEmpty from "lodash/isEmpty";
import { GapDocumentationModal } from "./GapDocumentationModal";
import { dataToFile } from "../../utils";
import isEqual from "lodash/isEqual";
import { formatDate } from "@certn/utils";
import { useContext } from "react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { css } from "@emotion/css";

type GapDocumentationProps = {
  index: number;
  gap_documentation: GapDocumentationType;
  schema: Record<string, JsonSchema4 | JsonSchema7>;
  onGapDocumentationChange: (gap_documentation: GapDocumentationType) => void;
};

export function GapDocumentation({
  index,
  gap_documentation,
  schema,
  onGapDocumentationChange,
}: GapDocumentationProps) {
  const { t } = useTranslation();
  const { submitErrors } = useContext(CascadiaJsonFormContext);
  const errors =
    submitErrors[`gap_history.gaps.${index}.gap_documentation`] ?? [];
  const gapDocumentTypes = schema.document_type.oneOf ?? [];

  const getDocumentTypeLabel = (value: string) =>
    gapDocumentTypes.find((t) => t.const === value)?.title ?? value;

  const onAdd = (document: GapDocumentationItem) => {
    onGapDocumentationChange([...gap_documentation, document]);
  };

  const onRemove = (document: GapDocumentationItem) => {
    onGapDocumentationChange(
      gap_documentation.filter((d) => !isEqual(d, document))
    );
  };

  return (
    <Stack gap="space-4" testID={`gap-documentation-${index}`}>
      <Stack gap="space-2">
        <Text
          testID={`gap-documentation-heading-${index}`}
          style="form/section-heading"
        >
          {t("Supporting documentation")}
        </Text>
        <Text
          testID={`gap-documentation-description-${index}`}
          style="form/body-secondary"
        >
          {t(
            "Please attach any relevant files or documents that support your statement."
          )}
        </Text>
      </Stack>
      {gap_documentation && !isEmpty(gap_documentation) && (
        <ActionList
          hideBorder
          children={
            <Stack gap="space-2">
              {gap_documentation.map((document) => {
                const file = dataToFile(document.gap_document_file);

                return (
                  <ActionListItem
                    key={document.gap_document_file}
                    title={file?.name}
                    className={css({
                      border: "1px solid var(--border-base)",
                      borderRadius: "var(--space-1)",
                    })}
                    trailing={
                      <Link
                        textColor="text-weak"
                        testID="remove-file-link"
                        textStyle="global/body-secondary"
                        onPress={() => onRemove(document)}
                      >
                        {t("Remove")}
                      </Link>
                    }
                    children={`${getDocumentTypeLabel(document.document_type)} • ${formatDate(document.document_date, "MMM dd, yyyy")}`}
                  />
                );
              })}
            </Stack>
          }
        />
      )}
      <ModalTrigger>
        <BasicButton size="medium" testID="save-and-exit-button" leftIcon="add">
          <Trans>Add document</Trans>
        </BasicButton>
        <Modal testID="gap-documentation-modal">
          <Dialog>
            {({ close }) => (
              <GapDocumentationModal
                close={close}
                schema={schema}
                submit={onAdd}
                existingGapDocumentFiles={gap_documentation.map(
                  (d) => d.gap_document_file
                )}
              />
            )}
          </Dialog>
        </Modal>
      </ModalTrigger>
      <RequirementsList
        requirements={[
          {
            text: t("Minimum 1 document"),
            style:
              !isEmpty(errors) && isEmpty(gap_documentation)
                ? "error"
                : isEmpty(gap_documentation)
                  ? "normal"
                  : "success",
          },
        ]}
      />
    </Stack>
  );
}
